// src/pages/InterviewPage.jsx
// Interview page with LiveAvatar FULL mode integration
// Flow: welcome -> preview -> connecting -> interview -> completed
// Added: Pause/disconnect handling

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import CapacityWait from '../components/CapacityWait';
import '../styles/InterviewPage.css';
import { 
  trackPageView,
  trackInterviewLoaded, 
  trackInterviewError, 
  trackConsentGiven,
  trackInterviewStarted, 
  trackInterviewPaused, 
  trackInterviewCompleted,
  trackRecordingUploaded 
} from '../utils/analytics';

const API_BASE = process.env.REACT_APP_API_BASE || 'https://api.invenire.app';

export default function InterviewPage() {
  const { token } = useParams();
  
  // Interview data
  const [interview, setInterview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [capacityWait, setCapacityWait] = useState(null);
  
  // Flow states: setup -> connecting -> interview -> completed
  const [stage, setStage] = useState('setup');
  
  // LiveAvatar session
  // eslint-disable-next-line no-unused-vars
  const [session, setSession] = useState(null);
  const [connectionStatus, setConnectionStatus] = useState('disconnected');
  
  // Media states
  const [cameraEnabled, setCameraEnabled] = useState(false);
  const [micEnabled, setMicEnabled] = useState(false);
  const [mediaStream, setMediaStream] = useState(null);
  const [mediaError, setMediaError] = useState(null);
  const [audioLevel, setAudioLevel] = useState(0);
  
  // Intro lock - mic disabled during avatar intro
  const [introComplete, setIntroComplete] = useState(false);
  
  // Track if media has been started
  const [mediaStarted, setMediaStarted] = useState(false);
  
  // Track if we ever connected successfully (to distinguish disconnect vs never connected)
  const [wasConnected, setWasConnected] = useState(false);
  
  // Recording consent and state
  const [recordingConsent, setRecordingConsent] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [recordingStatus, setRecordingStatus] = useState('idle'); // idle, recording, uploading, complete, error
  const [showAccommodationsModal, setShowAccommodationsModal] = useState(false);
  const [currentQuestionText, setCurrentQuestionText] = useState('');
  
  // Accessibility accommodations
  const [accommodations, setAccommodations] = useState({
    extendedTime: false,
    timeMultiplier: 1,
    textQuestions: false,
    textResponses: false,
    screenReader: false,
    largeText: false,
    highContrast: false,
    reducedMotion: false,
    otherAccommodations: ''
  });
  // eslint-disable-next-line no-unused-vars
  const [showAccommodationsForm, setShowAccommodationsForm] = useState(false);

  // Pause state
  const [isPaused, setIsPaused] = useState(false);
  const [pauseReason, setPauseReason] = useState(null); // 'user_paused' or 'disconnected'
  const [pausing, setPausing] = useState(false);
  
  // Question tracking state - for reconnect flow
  // eslint-disable-next-line no-unused-vars
  const [questionsAsked, setQuestionsAsked] = useState([]);
  // eslint-disable-next-line no-unused-vars
  const [blueprintQuestions, setBlueprintQuestions] = useState([]);
  
  // Multi-session recording support
  const [sessionNumber, setSessionNumber] = useState(1);
  
  // Refs
  const videoPreviewRef = useRef(null);
  const avatarVideoRef = useRef(null);
  const audioContextRef = useRef(null);
  const analyserRef = useRef(null);
  const animationFrameRef = useRef(null);
  const roomRef = useRef(null);
  const mediaStreamRef = useRef(null);  // ref for reliable mic access
  const publishedAudioTrackRef = useRef(null);  // ref for published track
  const keepAliveIntervalRef = useRef(null);  // keep-alive ping interval
  const sessionRef = useRef(null);  // session data for keep-alive
  
  // Recording refs
  const mediaRecorderRef = useRef(null);
  const recordedChunksRef = useRef([]);
  const recordingCanvasRef = useRef(null);
  const canvasAnimationRef = useRef(null);
  const startRecordingRef = useRef(null);  // ref to call startRecording from earlier hook
  const avatarAudioTrackRef = useRef(null);  // ref for avatar's remote audio track
  const recordingAudioContextRef = useRef(null);  // AudioContext for mixing audio
  const completeInterviewRef = useRef(null);  // ref to call completeInterview from event handlers
  const disconnectTimeoutRef = useRef(null);  // timeout before completing on disconnect
  const reconnectAttemptsRef = useRef(0);  // track reconnection attempts
  const pauseInterviewRef = useRef(null);  // ref to call pauseInterview from event handlers
  
  // Question tracking refs
  const questionsAskedRef = useRef([]);  // ref for reliable access in callbacks
  const currentQuestionRef = useRef(null);  // track current question awaiting response
  const blueprintQuestionsRef = useRef([]);  // ref for blueprint questions
  const realTimeTranscriptRef = useRef([]);  // Full transcript with correct speaker labels from HeyGen
  const interviewStartTimeRef = useRef(null);  // Track when interview started for duration calculation

  // =============================================================================
  // LOAD INTERVIEW DATA
  // =============================================================================
  
  useEffect(() => {
    async function loadInterview() {
      try {
        const response = await fetch(`${API_BASE}/invites/${token}`);
        
        if (!response.ok) {
          throw new Error('Failed to load interview');
        }
        
        const data = await response.json();
        
        if (!data.valid) {
          setError(data.error || 'Interview not found');
          trackInterviewError(null, data.error || 'Interview not found');
          return;
        }
        
        setInterview(data);
        
        // Track successful interview load
        trackPageView('/i/[token]', `Interview - ${data.company_name}`);
        trackInterviewLoaded(data.interview_id, data.company_name, data.job_title);
      } catch (err) {
        console.error('Error loading interview:', err);
        setError('Unable to load interview. Please check your link and try again.');
        trackInterviewError(null, err.message);
      } finally {
        setLoading(false);
      }
    }
    
    if (token) {
      loadInterview();
    } else {
      setError('No interview token provided');
      setLoading(false);
    }
  }, [token]);

  // =============================================================================
  // QUESTION TRACKING HELPERS
  // =============================================================================
  
  // Fuzzy match avatar speech against blueprint questions
  // Returns {question, index} if matched, null otherwise
  const findMatchingQuestion = useCallback((avatarText, questions) => {
    if (!avatarText || !questions || questions.length === 0) return null;
    
    const normalizedAvatar = avatarText.toLowerCase().trim();
    
    // Skip intro/closing phrases
    const skipPhrases = ['thanks for', 'thank you', 'good luck', 'nice to meet', 'welcome', 'great to'];
    if (skipPhrases.some(phrase => normalizedAvatar.includes(phrase))) {
      return null;
    }
    
    for (let i = 0; i < questions.length; i++) {
      const q = questions[i];
      const questionText = typeof q === 'string' ? q : q.question || q.text || '';
      const normalizedQ = questionText.toLowerCase().trim();
      
      // Check if avatar text contains substantial portion of the question
      // (avatar might add context or rephrase slightly)
      const questionWords = normalizedQ.split(/\s+/).filter(w => w.length > 3);
      const matchedWords = questionWords.filter(word => normalizedAvatar.includes(word));
      const matchRatio = matchedWords.length / questionWords.length;
      
      // If more than 60% of significant words match, consider it a match
      if (matchRatio > 0.6) {
        console.log(`[Question Match] Ratio ${matchRatio.toFixed(2)} for question ${i}: "${questionText.substring(0, 50)}..."`);
        return { question: questionText, index: i };
      }
    }
    
    return null;
  }, []);
  
  // Fetch blueprint questions when interview is loaded
  useEffect(() => {
    async function fetchBlueprint() {
      if (!interview?.interview_id) return;
      
      try {
        const response = await fetch(`${API_BASE}/interviews/${interview.interview_id}/blueprint`);
        if (response.ok) {
          const data = await response.json();
          const questions = data.questions || [];
          console.log('[Blueprint] Loaded questions:', questions.length);
          setBlueprintQuestions(questions);
          blueprintQuestionsRef.current = questions;
        }
      } catch (err) {
        console.warn('Failed to load blueprint:', err);
        // Not critical - question tracking will still work without blueprint
      }
    }
    
    fetchBlueprint();
  }, [interview?.interview_id]);

  // Fetch current session number for multi-session recordings
  useEffect(() => {
    async function fetchSessionInfo() {
      if (!interview?.interview_id) return;
      
      try {
        // Get recording info which includes session count
        const response = await fetch(`${API_BASE}/recordings/${interview.interview_id}`);
        if (response.ok) {
          const data = await response.json();
          // If there are existing session recordings, set session number to next
          const existingSessions = data.session_recordings || [];
          const nextSession = existingSessions.length + 1;
          console.log('[Recording] Session number:', nextSession, '(existing sessions:', existingSessions.length, ')');
          setSessionNumber(nextSession);
        }
      } catch (err) {
        console.warn('Failed to fetch session info:', err);
        // Default to session 1 if we can't fetch
      }
    }
    
    fetchSessionInfo();
  }, [interview?.interview_id]);

  // =============================================================================
  // CLEANUP
  // =============================================================================
  
  useEffect(() => {
    return () => {
      // Stop media tracks
      if (mediaStream) {
        mediaStream.getTracks().forEach(track => track.stop());
      }
      // Close audio context (for mic level meter)
      if (audioContextRef.current) {
        audioContextRef.current.close();
      }
      // Close recording audio context (for audio mixing)
      if (recordingAudioContextRef.current) {
        recordingAudioContextRef.current.close();
      }
      // Cancel animation frame
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
      // Cancel canvas animation frame
      if (canvasAnimationRef.current) {
        cancelAnimationFrame(canvasAnimationRef.current);
      }
      // Stop media recorder
      if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
        mediaRecorderRef.current.stop();
      }
      // Clear keep-alive interval
      if (keepAliveIntervalRef.current) {
        clearInterval(keepAliveIntervalRef.current);
      }
      // Clear disconnect timeout
      if (disconnectTimeoutRef.current) {
        clearTimeout(disconnectTimeoutRef.current);
      }
      // Clear session ref
      sessionRef.current = null;
      // Disconnect from LiveKit room
      if (roomRef.current) {
        roomRef.current.disconnect();
      }
    };
  }, [mediaStream]);

  // Update video preview when stream or stage changes
  useEffect(() => {
    const stream = mediaStreamRef.current || mediaStream;
    if (stream && videoPreviewRef.current) {
      videoPreviewRef.current.srcObject = stream;
      console.log('Video srcObject set for stage:', stage);
    }
  }, [mediaStream, stage]);
  
  // Re-attach video when interview stage mounts (with retry)
  useEffect(() => {
    if (stage === 'interview') {
      const attachVideo = () => {
        const stream = mediaStreamRef.current;
        if (stream && videoPreviewRef.current) {
          videoPreviewRef.current.srcObject = stream;
          console.log('Video re-attached for interview stage');
          return true;
        }
        return false;
      };
      
      // Try immediately
      if (!attachVideo()) {
        // Retry a few times with increasing delays
        const timers = [
          setTimeout(attachVideo, 100),
          setTimeout(attachVideo, 300),
          setTimeout(attachVideo, 500),
        ];
        return () => timers.forEach(clearTimeout);
      }
    }
  }, [stage]);

  // =============================================================================
  // AUDIO LEVEL MONITORING
  // =============================================================================
  
  const setupAudioMonitoring = useCallback(async (stream) => {
    try {
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      
      if (audioContext.state === 'suspended') {
        await audioContext.resume();
      }
      
      const analyser = audioContext.createAnalyser();
      analyser.fftSize = 512;
      
      const source = audioContext.createMediaStreamSource(stream);
      source.connect(analyser);
      
      audioContextRef.current = audioContext;
      analyserRef.current = analyser;
      
      const dataArray = new Uint8Array(analyser.frequencyBinCount);
      
      const updateLevel = () => {
        if (!analyserRef.current) return;
        
        // Use getByteTimeDomainData for voice detection (not frequency)
        analyserRef.current.getByteTimeDomainData(dataArray);
        
        // Calculate RMS from time domain data
        let sumSquares = 0;
        for (let i = 0; i < dataArray.length; i++) {
          const v = (dataArray[i] - 128) / 128; // Normalize to -1 to 1
          sumSquares += v * v;
        }
        const rms = Math.sqrt(sumSquares / dataArray.length);
        
        // Scale for display (rms is typically 0-0.5 for speech)
        const normalizedLevel = Math.min(100, rms * 400);
        
        setAudioLevel(normalizedLevel);
        animationFrameRef.current = requestAnimationFrame(updateLevel);
      };
      
      updateLevel();
    } catch (err) {
      console.error('Audio monitoring error:', err);
    }
  }, []);

  // =============================================================================
  // MEDIA SETUP
  // =============================================================================
  
  const startMedia = useCallback(async () => {
    try {
      setMediaError(null);
      
      const stream = await navigator.mediaDevices.getUserMedia({
        video: true,
        audio: true
      });
      
      setMediaStream(stream);
      mediaStreamRef.current = stream;  // ADDED: Store in ref for reliable access
      setCameraEnabled(true);
      setMicEnabled(true);
      
      await setupAudioMonitoring(stream);
      
    } catch (err) {
      console.error('Media access error:', err);
      if (err.name === 'NotAllowedError') {
        setMediaError('Camera/microphone access denied. Please allow access and try again.');
      } else if (err.name === 'NotFoundError') {
        setMediaError('No camera or microphone found. Please connect a device and try again.');
      } else {
        setMediaError(`Media error: ${err.message}`);
      }
    }
  }, [setupAudioMonitoring]);

  // Auto-start media when interview loads (setup stage)
  useEffect(() => {
    if (interview && stage === 'setup' && !mediaStarted && !mediaStream) {
      setMediaStarted(true);
      startMedia();
    }
  }, [interview, stage, mediaStarted, mediaStream, startMedia]);

  const toggleCamera = useCallback(() => {
    const stream = mediaStreamRef.current || mediaStream;
    if (stream) {
      const videoTrack = stream.getVideoTracks()[0];
      if (videoTrack) {
        videoTrack.enabled = !videoTrack.enabled;
        setCameraEnabled(videoTrack.enabled);
      }
    }
  }, [mediaStream]);

  const toggleMic = useCallback(() => {
    // Don't allow mic toggle during intro
    if (!introComplete && stage === 'interview') {
      console.log('Mic toggle blocked - intro not complete');
      return;
    }
    
    // Use ref for reliable track access
    const track = publishedAudioTrackRef.current || mediaStreamRef.current?.getAudioTracks()[0];
    if (track) {
      track.enabled = !track.enabled;
      setMicEnabled(track.enabled);
      console.log('Mic toggled:', track.enabled);
    }
  }, [introComplete, stage]);

  // =============================================================================
  // LIVEKIT CONNECTION
  // =============================================================================
  
  const connectToLiveKit = useCallback(async (livekitUrl, livekitToken) => {
    try {
      setConnectionStatus('connecting');
      
      // Set stage to interview FIRST so the video element exists
      setStage('interview');
      
      // Small delay to ensure React renders the video element
      await new Promise(resolve => setTimeout(resolve, 100));
      
      // Dynamic import of LiveKit
      const { Room, RoomEvent, VideoPresets, ConnectionState } = await import('livekit-client');
      
      const room = new Room({
        adaptiveStream: true,
        dynacast: true,
        // Reconnection settings
        reconnectPolicy: {
          maxRetries: 10,
          nextRetryDelayInMs: (context) => {
            // Exponential backoff: 300ms, 600ms, 1200ms, etc. up to 10s
            return Math.min(300 * Math.pow(2, context.retryCount), 10000);
          }
        },
        disconnectOnPageLeave: false,
        videoCaptureDefaults: {
          resolution: VideoPresets.h720.resolution,
        },
      });
      
      roomRef.current = room;
      
      // ===== DIAGNOSTIC EVENT LISTENERS =====
      
      // Track signal connection quality
      room.on(RoomEvent.SignalConnected, () => {
        console.log('[LiveKit] Signal connected');
      });
      
      room.on(RoomEvent.SignalReconnecting, () => {
        console.log('[LiveKit] Signal reconnecting...');
      });
      
      // Connection quality monitoring
      room.on(RoomEvent.ConnectionQualityChanged, (quality, participant) => {
        console.log('[LiveKit] Connection quality:', quality, 'for', participant?.identity || 'local');
      });
      
      // Track publication issues
      room.on(RoomEvent.LocalTrackUnpublished, (publication, participant) => {
        console.log('[LiveKit] Local track unpublished:', publication.trackName);
      });
      
      // Media device errors
      room.on(RoomEvent.MediaDevicesError, (error) => {
        console.error('[LiveKit] Media device error:', error);
      });
      
      // ===== END DIAGNOSTIC LISTENERS =====
      
      // ===== LIVEAVATAR EVENT LISTENERS =====
      // LiveAvatar sends events through LiveKit data channel including transcription
      room.on(RoomEvent.DataReceived, (data, participant) => {
        try {
          const message = JSON.parse(new TextDecoder().decode(data));
          console.log('[LiveAvatar Event]', message.event_type || message.type, message);
          
          // Handle transcription events - track for reconnect flow
          const eventType = message.event_type || message.type;
          
          if (eventType === 'avatar.transcription' || eventType === 'avatar.response') {
            const avatarText = message.text || '';
            console.log('[Transcript] Avatar said:', avatarText);
            
            // Add to real-time transcript with correct label
            if (avatarText.trim()) {
              realTimeTranscriptRef.current.push({
                role: 'interviewer',
                text: avatarText,
                timestamp: Date.now()
              });
            }
            
            // Check if this matches a blueprint question (fuzzy match)
            // Check if this matches a blueprint question (fuzzy match)
            const matchedQuestion = findMatchingQuestion(avatarText, blueprintQuestionsRef.current);
            if (matchedQuestion) {
              console.log('[Question Tracking] Matched question:', matchedQuestion.question);
              currentQuestionRef.current = {
                question: matchedQuestion.question,
                questionIndex: matchedQuestion.index,
                avatarText: avatarText,
                timestamp: Date.now()
              };
              // Update state for text display overlay (accessibility)
              setCurrentQuestionText(matchedQuestion.question);
            }
          } else if (eventType === 'user.transcription' || eventType === 'user.transcript') {
            const userText = message.text || '';
            console.log('[Transcript] User said:', userText);
            
            // Add to real-time transcript with correct label
            if (userText.trim()) {
              realTimeTranscriptRef.current.push({
                role: 'candidate',
                text: userText,
                timestamp: Date.now()
              });
            }
            
            // If we have a pending question, record this as the response
            if (currentQuestionRef.current && userText.trim().length > 10) {
              const qaPair = {
                ...currentQuestionRef.current,
                response: userText,
                responseTimestamp: Date.now()
              };
              console.log('[Question Tracking] Recording Q&A pair:', qaPair);
              
              // Update both ref and state
              questionsAskedRef.current = [...questionsAskedRef.current, qaPair];
              setQuestionsAsked(prev => [...prev, qaPair]);
              
              // Clear current question
              currentQuestionRef.current = null;
            }
          } else if (eventType === 'user.audio_start') {
            console.log('[Audio] User started speaking');
          } else if (eventType === 'user.audio_end') {
            console.log('[Audio] User stopped speaking');
          } else if (eventType === 'avatar.audio_start') {
            console.log('[Audio] Avatar started speaking');
          } else if (eventType === 'avatar.audio_end') {
            console.log('[Audio] Avatar stopped speaking');
          }
        } catch (e) {
          // Not JSON or parsing error - might be binary data
          console.log('[LiveKit Data] Non-JSON data received');
        }
      });
      // ===== END LIVEAVATAR LISTENERS =====
      
      // Handle remote participant (the avatar)
      room.on(RoomEvent.TrackSubscribed, (track, publication, participant) => {
        console.log('Track subscribed:', track.kind, participant.identity);
        
        if (track.kind === 'video') {
          if (avatarVideoRef.current) {
            track.attach(avatarVideoRef.current);
            console.log('Video track attached to element');
          } else {
            setTimeout(() => {
              if (avatarVideoRef.current) {
                track.attach(avatarVideoRef.current);
              }
            }, 500);
          }
        }
        
        if (track.kind === 'audio') {
          const audioElement = track.attach();
          audioElement.volume = 1.0;
          document.body.appendChild(audioElement);
          console.log('Audio track attached');
          
          // Store the audio track for recording
          // LiveKit tracks have a mediaStreamTrack property
          if (track.mediaStreamTrack) {
            avatarAudioTrackRef.current = track.mediaStreamTrack;
            console.log('Avatar audio track stored for recording');
          }
        }
      });
      
      room.on(RoomEvent.TrackUnsubscribed, (track) => {
        track.detach();
      });
      
      // Connection state changes
      room.on(RoomEvent.ConnectionStateChanged, (state) => {
        console.log('Connection state changed:', state);
        if (state === ConnectionState.Connected) {
          setConnectionStatus('connected');
        } else if (state === ConnectionState.Reconnecting) {
          setConnectionStatus('reconnecting');
        } else if (state === ConnectionState.Disconnected) {
          setConnectionStatus('disconnected');
        }
      });
      
      room.on(RoomEvent.Reconnecting, () => {
        console.log('Reconnecting to room...');
        setConnectionStatus('reconnecting');
        reconnectAttemptsRef.current += 1;
        console.log('Reconnect attempt #', reconnectAttemptsRef.current);
        
        // Clear any pending disconnect timeout - we're trying to reconnect
        if (disconnectTimeoutRef.current) {
          clearTimeout(disconnectTimeoutRef.current);
          disconnectTimeoutRef.current = null;
        }
      });
      
      room.on(RoomEvent.Reconnected, () => {
        console.log('Reconnected to room successfully!');
        setConnectionStatus('connected');
        reconnectAttemptsRef.current = 0;
        
        // Clear any pending disconnect timeout
        if (disconnectTimeoutRef.current) {
          clearTimeout(disconnectTimeoutRef.current);
          disconnectTimeoutRef.current = null;
        }
      });
      
      room.on(RoomEvent.Disconnected, (reason) => {
        console.log('=== ROOM DISCONNECTED ===');
        console.log('Disconnect reason:', reason);
        console.log('Reconnect attempts made:', reconnectAttemptsRef.current);
        console.log('Room state:', room.state);
        setConnectionStatus('disconnected');
        
        // Clear keep-alive interval on disconnect
        if (keepAliveIntervalRef.current) {
          clearInterval(keepAliveIntervalRef.current);
          keepAliveIntervalRef.current = null;
        }
        sessionRef.current = null;
        
        // DON'T immediately complete - wait to see if this is a temporary disconnect
        // LiveKit fires Disconnected when reconnection fails after max attempts
        // We'll give it a grace period before pausing (not completing)
        if (mediaRecorderRef.current?.state === 'recording') {
          console.log('Recording in progress - waiting 10s before pausing in case of temporary disconnect...');
          
          // Clear any existing timeout
          if (disconnectTimeoutRef.current) {
            clearTimeout(disconnectTimeoutRef.current);
          }
          
          disconnectTimeoutRef.current = setTimeout(() => {
            console.log('Disconnect timeout reached - pausing interview and saving recording');
            // Use pauseInterview instead of completeInterview for disconnects
            if (pauseInterviewRef.current) {
              pauseInterviewRef.current('disconnected');
            } else if (completeInterviewRef.current) {
              completeInterviewRef.current();
            }
          }, 10000); // Wait 10 seconds before giving up
        }
      });
      
      room.on(RoomEvent.Connected, () => {
        console.log('Connected to room');
        setConnectionStatus('connected');
        setWasConnected(true);
      });
      
      // Handle participant disconnection (avatar left)
      room.on(RoomEvent.ParticipantDisconnected, (participant) => {
        console.log('=== PARTICIPANT DISCONNECTED ===');
        console.log('Participant identity:', participant.identity);
        console.log('Participant SID:', participant.sid);
        console.log('Room participants remaining:', room.remoteParticipants.size);
        
        // If the avatar disconnected, the interview is likely over
        // But wait a moment to see if they reconnect
        if (participant.identity.includes('avatar') || participant.identity.includes('agent')) {
          console.log('Avatar/agent disconnected - waiting 5s to see if they reconnect...');
          
          // Clear any existing timeout
          if (disconnectTimeoutRef.current) {
            clearTimeout(disconnectTimeoutRef.current);
          }
          
          disconnectTimeoutRef.current = setTimeout(() => {
            // Check if avatar has reconnected
            let avatarReconnected = false;
            room.remoteParticipants.forEach((p) => {
              if (p.identity.includes('avatar') || p.identity.includes('agent')) {
                avatarReconnected = true;
              }
            });
            
            if (avatarReconnected) {
              console.log('Avatar reconnected - continuing interview');
            } else {
              console.log('Avatar did not reconnect - completing interview');
              if (completeInterviewRef.current) {
                completeInterviewRef.current();
              } else {
                console.warn('completeInterviewRef not set, falling back to direct completion');
                fetch(`${API_BASE}/invites/${token}/complete`, { method: 'POST' })
                  .catch(err => console.warn('Failed to mark complete:', err));
                setStage('completed');
              }
            }
          }, 5000); // Wait 5 seconds before giving up on avatar
        }
      });
      
      // Connect to the room
      await room.connect(livekitUrl, livekitToken);
      console.log('Connected to LiveKit room');
      
      // Start keep-alive ping to prevent timeout during long answers
      // The LiveAvatar API has an idle timeout that closes inactive sessions
      // We call the keep-alive endpoint every 30 seconds to refresh the timer
      if (keepAliveIntervalRef.current) {
        clearInterval(keepAliveIntervalRef.current);
      }
      
      let keepAliveCount = 0;
      keepAliveIntervalRef.current = setInterval(async () => {
        const sessionToken = sessionRef.current?.session_token;
        const sessionId = sessionRef.current?.session_id;
        keepAliveCount++;
        
        if (sessionToken && room.state === 'connected') {
          try {
            console.log(`[Keep-Alive #${keepAliveCount}] Sending ping...`);
            const params = new URLSearchParams({ session_token: sessionToken });
            if (sessionId) params.append('session_id', sessionId);
            
            const response = await fetch(`${API_BASE}/avatar/session/keep-alive?${params}`, {
              method: 'POST'
            });
            const result = await response.json();
            
            if (result.status === 'alive') {
              console.log(`[Keep-Alive #${keepAliveCount}] SUCCESS`);
            } else {
              console.warn(`[Keep-Alive #${keepAliveCount}] Response:`, result);
            }
          } catch (err) {
            console.error(`[Keep-Alive #${keepAliveCount}] FAILED:`, err);
          }
        } else {
          console.log(`[Keep-Alive #${keepAliveCount}] Skipped - no session or disconnected`);
        }
      }, 30000); // Every 30 seconds
      
      // Publish mic track (will be muted during intro)
      const stream = mediaStreamRef.current;
      if (stream) {
        const audioTrack = stream.getAudioTracks()[0];
        if (audioTrack) {
          // Publish to LiveKit first
          await room.localParticipant.publishTrack(audioTrack, {
            name: 'microphone',
            source: 'microphone'
          });
          publishedAudioTrackRef.current = audioTrack;
          
          // NOW mute the track after publishing
          audioTrack.enabled = false;
          setMicEnabled(false);
          console.log('Audio track published to LiveKit, then MUTED for intro');
          console.log('Track enabled state after muting:', audioTrack.enabled);
        }
      }
      
      setConnectionStatus('connected');
      setWasConnected(true);
      
      // Track interview started and record start time
      interviewStartTimeRef.current = Date.now();
      trackInterviewStarted(interview?.interview_id, sessionNumber);
      
      // Unlock mic after intro - use dynamic duration from backend
      // Based on intro script word count: (words / 3.5) + 3 seconds
      const sessionData = sessionRef.current;
      const introDuration = sessionData?.intro_duration_seconds || 30;
      const INTRO_DURATION_MS = introDuration * 1000;
      console.log(`[Intro Lockout] intro_duration_seconds from backend: ${sessionData?.intro_duration_seconds}`);
      console.log(`[Intro Lockout] Mic will be enabled after ${introDuration} seconds (${INTRO_DURATION_MS}ms)`);
      
      // Periodically ensure track stays muted during intro (in case something re-enables it)
      const muteCheckInterval = setInterval(() => {
        const track = publishedAudioTrackRef.current;
        if (track && track.enabled) {
          console.warn('[Intro Lockout] Track was re-enabled! Muting again...');
          track.enabled = false;
          setMicEnabled(false);
        }
      }, 1000);
      
      setTimeout(() => {
        // Stop the mute check
        clearInterval(muteCheckInterval);
        
        console.log('[Intro Lockout] Timer fired - enabling mic now');
        setIntroComplete(true);
        
        // Enable mic using ref for reliable access
        const track = publishedAudioTrackRef.current;
        if (track) {
          track.enabled = true;
          setMicEnabled(true);
          console.log('Intro complete - mic ENABLED, track.enabled =', track.enabled);
        } else {
          console.warn('No published audio track found to enable');
          // Still mark intro as complete so UI updates
          setMicEnabled(true);
        }
      }, INTRO_DURATION_MS);
      
    } catch (err) {
      console.error('LiveKit connection error:', err);
      setConnectionStatus('error');
      setError(`Failed to connect to interview: ${err.message}`);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);  // No dependencies - uses refs for stability

  // =============================================================================
  // START INTERVIEW
  // =============================================================================
  
  const proceedToConsent = useCallback(() => {
  setStage('consent');
  }, []);
  
  // Actually start the interview (called after consent)
  const startInterview = useCallback(async () => {
    if (!interview?.interview_id) {
      setError('No interview ID available');
      return;
    }
    
    if (!recordingConsent) {
      setError('Recording consent is required to proceed');
      return;
    }
    
    setStage('connecting');
    setConnectionStatus('starting');
    
    // Track consent given
    trackConsentGiven(interview.interview_id);
    
    try {
      // Record consent on backend
      await fetch(`${API_BASE}/recordings/consent`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          interview_id: interview.interview_id,
          token: token
        })
      }).catch(err => console.warn('Failed to record consent:', err));
      
      // Check capacity and mark interview as started
      const joinResponse = await fetch(`${API_BASE}/interviews/join`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          interview_id: interview.interview_id,
          token: token
        })
      });
      
      const joinData = await joinResponse.json();
      
      // Check if we hit capacity limit
      if (joinData.status === 'awaiting_capacity') {
        setCapacityWait({
          job_title: interview.job_title,
          company_name: interview.company_name,
          candidate_email: interview.candidate_email,
          message: joinData.message
        });
        setStage('setup'); // Reset stage
        return;
      }
      
      // Start LiveAvatar session
      const response = await fetch(`${API_BASE}/avatar/interview/start`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ interview_id: interview.interview_id })
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to start interview session');
      }
      
      const sessionData = await response.json();
      console.log('[Session] Avatar start response:', sessionData);
      console.log('[Session] intro_duration_seconds:', sessionData.intro_duration_seconds);
      setSession(sessionData);
      sessionRef.current = sessionData;  // Also store in ref for keep-alive access
      
      // Connect to LiveKit
      await connectToLiveKit(sessionData.livekit_url, sessionData.livekit_client_token);
      
      // Start recording after a short delay (to ensure video elements are ready)
      setTimeout(() => {
        if (startRecordingRef.current) {
          startRecordingRef.current();
        }
      }, 2000);
      
    } catch (err) {
      console.error('Error starting interview:', err);
      setError(`Failed to start interview: ${err.message}`);
      setStage('setup');
    }
  }, [interview, token, connectToLiveKit, recordingConsent]);

  // =============================================================================
  // RECORDING FUNCTIONS
  // =============================================================================
  
  // Start recording - creates a canvas that composites both videos
  const startRecording = useCallback(() => {
    try {
      // Create canvas for compositing
      const canvas = document.createElement('canvas');
      canvas.width = 1280;
      canvas.height = 720;
      recordingCanvasRef.current = canvas;
      const ctx = canvas.getContext('2d');
      
      // Animation loop to draw both videos to canvas
      const drawFrame = () => {
        if (!recordingCanvasRef.current) return;
        
        // Clear canvas
        ctx.fillStyle = '#0a0f14';
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        
        // Draw avatar video (main - left side, larger)
        const avatarVideo = avatarVideoRef.current;
        if (avatarVideo && avatarVideo.videoWidth > 0) {
          // Avatar takes 75% of width
          const avatarWidth = canvas.width * 0.75;
          const avatarHeight = canvas.height;
          ctx.drawImage(avatarVideo, 0, 0, avatarWidth, avatarHeight);
        }
        
        // Draw self video (picture-in-picture - bottom right)
        const selfVideo = videoPreviewRef.current;
        if (selfVideo && selfVideo.videoWidth > 0) {
          const pipWidth = canvas.width * 0.22;
          const pipHeight = pipWidth * (9/16);
          const pipX = canvas.width - pipWidth - 20;
          const pipY = canvas.height - pipHeight - 20;
          
          // Draw border
          ctx.strokeStyle = '#3ECF8E';
          ctx.lineWidth = 3;
          ctx.strokeRect(pipX - 2, pipY - 2, pipWidth + 4, pipHeight + 4);
          
          ctx.drawImage(selfVideo, pipX, pipY, pipWidth, pipHeight);
        }
        
        canvasAnimationRef.current = requestAnimationFrame(drawFrame);
      };
      
      drawFrame();
      
      // Get canvas stream
      const canvasStream = canvas.captureStream(30); // 30 FPS
      
      // Mix audio from both local mic AND avatar using Web Audio API
      try {
        const audioContext = new (window.AudioContext || window.webkitAudioContext)();
        recordingAudioContextRef.current = audioContext;
        
        // Create a destination node - this will be our mixed audio output
        const destination = audioContext.createMediaStreamDestination();
        
        // Add local mic audio
        const localStream = mediaStreamRef.current;
        if (localStream && localStream.getAudioTracks().length > 0) {
          const localSource = audioContext.createMediaStreamSource(localStream);
          localSource.connect(destination);
          console.log('Local mic audio connected to recording mixer');
        }
        
        // Add avatar audio (remote track from LiveKit)
        const avatarAudioTrack = avatarAudioTrackRef.current;
        if (avatarAudioTrack) {
          const avatarStream = new MediaStream([avatarAudioTrack]);
          const avatarSource = audioContext.createMediaStreamSource(avatarStream);
          avatarSource.connect(destination);
          console.log('Avatar audio connected to recording mixer');
        } else {
          console.warn('Avatar audio track not available for recording');
        }
        
        // Add the mixed audio track to the canvas stream
        const mixedAudioTracks = destination.stream.getAudioTracks();
        mixedAudioTracks.forEach(track => {
          canvasStream.addTrack(track);
        });
        console.log('Mixed audio track added to recording stream');
        
      } catch (audioErr) {
        console.error('Failed to set up audio mixing:', audioErr);
        // Fallback: just use local audio
        const localStream = mediaStreamRef.current;
        if (localStream) {
          const audioTracks = localStream.getAudioTracks();
          audioTracks.forEach(track => {
            canvasStream.addTrack(track.clone());
          });
        }
      }
      
      // Create MediaRecorder
      const options = { 
        mimeType: 'video/webm;codecs=vp9,opus',
        videoBitsPerSecond: 2500000  // 2.5 Mbps
      };
      
      // Fallback if vp9 not supported
      if (!MediaRecorder.isTypeSupported(options.mimeType)) {
        options.mimeType = 'video/webm;codecs=vp8,opus';
      }
      if (!MediaRecorder.isTypeSupported(options.mimeType)) {
        options.mimeType = 'video/webm';
      }
      
      const mediaRecorder = new MediaRecorder(canvasStream, options);
      mediaRecorderRef.current = mediaRecorder;
      recordedChunksRef.current = [];
      
      mediaRecorder.ondataavailable = (event) => {
        if (event.data && event.data.size > 0) {
          recordedChunksRef.current.push(event.data);
        }
      };
      
      mediaRecorder.onstart = () => {
        console.log('Recording started');
        setIsRecording(true);
        setRecordingStatus('recording');
      };
      
      mediaRecorder.onstop = () => {
        console.log('Recording stopped, chunks:', recordedChunksRef.current.length);
        setIsRecording(false);
        // Canvas animation cleanup
        if (canvasAnimationRef.current) {
          cancelAnimationFrame(canvasAnimationRef.current);
          canvasAnimationRef.current = null;
        }
      };
      
      mediaRecorder.onerror = (event) => {
        console.error('Recording error:', event);
        setRecordingStatus('error');
      };
      
      // Start recording with 1-second chunks
      mediaRecorder.start(1000);
      
    } catch (err) {
      console.error('Failed to start recording:', err);
      setRecordingStatus('error');
    }
  }, []);
  
  // Store startRecording in ref so it can be called from earlier hooks
  useEffect(() => {
    startRecordingRef.current = startRecording;
  }, [startRecording]);
  
  // Stop recording and get blob
  const stopRecording = useCallback(() => {
    return new Promise((resolve) => {
      const mediaRecorder = mediaRecorderRef.current;
      
      if (!mediaRecorder || mediaRecorder.state === 'inactive') {
        resolve(null);
        return;
      }
      
      mediaRecorder.onstop = () => {
        console.log('Recording stopped, creating blob...');
        const blob = new Blob(recordedChunksRef.current, { type: 'video/webm' });
        console.log('Recording blob size:', blob.size);
        
        // Cleanup canvas animation
        if (canvasAnimationRef.current) {
          cancelAnimationFrame(canvasAnimationRef.current);
          canvasAnimationRef.current = null;
        }
        
        // Cleanup audio context
        if (recordingAudioContextRef.current) {
          recordingAudioContextRef.current.close().catch(() => {});
          recordingAudioContextRef.current = null;
        }
        
        setIsRecording(false);
        resolve(blob);
      };
      
      mediaRecorder.stop();
    });
  }, []);
  
  // Helper to trigger transcription after upload
  const triggerTranscription = useCallback(async () => {
    console.log('Starting transcription for interview:', interview.interview_id);
    try {
      const transcriptionResponse = await fetch(`${API_BASE}/transcription/start`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ interview_id: interview.interview_id })
      });
      
      if (transcriptionResponse.ok) {
        const transcriptionResult = await transcriptionResponse.json();
        console.log('Transcription started:', transcriptionResult);
      } else {
        console.warn('Failed to start transcription:', await transcriptionResponse.text());
      }
    } catch (transcriptionErr) {
      console.warn('Error starting transcription:', transcriptionErr);
      // Don't fail - transcription can be retried later
    }
  }, [interview]);
  
  // Upload recording to Supabase Storage
  const uploadRecording = useCallback(async (blob) => {
    if (!blob || blob.size === 0) {
      console.warn('No recording to upload');
      return null;
    }
    
    setRecordingStatus('uploading');
    const fileName = `interview_${interview.interview_id}_${Date.now()}.webm`;
    const fileSizeMB = blob.size / (1024 * 1024);
    console.log(`Recording size: ${fileSizeMB.toFixed(2)} MB`);
    
    try {
      // For very large files (>50MB), use chunked upload
      if (blob.size > 50 * 1024 * 1024) {
        console.log('Very large file detected, using chunked upload...');
        
        const CHUNK_SIZE = 10 * 1024 * 1024; // 10MB chunks
        const totalChunks = Math.ceil(blob.size / CHUNK_SIZE);
        console.log(`Uploading in ${totalChunks} chunks of ${CHUNK_SIZE / (1024 * 1024)}MB each`);
        
        for (let i = 0; i < totalChunks; i++) {
          const start = i * CHUNK_SIZE;
          const end = Math.min(start + CHUNK_SIZE, blob.size);
          const chunk = blob.slice(start, end);
          
          const formData = new FormData();
          formData.append('interview_id', interview.interview_id.toString());
          formData.append('token', interview.token);
          formData.append('chunk_index', i.toString());
          formData.append('total_chunks', totalChunks.toString());
          formData.append('file_name', fileName);
          formData.append('session_number', sessionNumber.toString());  // Multi-session support
          formData.append('file', chunk, `chunk_${i}`);
          
          console.log(`Uploading chunk ${i + 1}/${totalChunks} (${((end - start) / (1024 * 1024)).toFixed(1)}MB)...`);
          
          const chunkResponse = await fetch(`${API_BASE}/recordings/upload-chunk`, {
            method: 'POST',
            body: formData
          });
          
          if (!chunkResponse.ok) {
            const errText = await chunkResponse.text();
            throw new Error(`Chunk ${i + 1} upload failed: ${errText}`);
          }
          
          const chunkResult = await chunkResponse.json();
          console.log(`Chunk ${i + 1}/${totalChunks} uploaded:`, chunkResult.status);
          
          // Update progress
          setRecordingStatus(`uploading (${Math.round(((i + 1) / totalChunks) * 100)}%)`);
          
          // If this was the last chunk, poll for assembly completion
          if (chunkResult.status === 'assembly_started' || (i === totalChunks - 1)) {
            console.log('All chunks uploaded, waiting for assembly...');
            setRecordingStatus('assembling');
            
            // Poll for assembly status
            const maxAttempts = 120; // 4 minutes max (2s intervals)
            for (let attempt = 0; attempt < maxAttempts; attempt++) {
              await new Promise(resolve => setTimeout(resolve, 2000)); // Wait 2 seconds
              
              try {
                const statusResponse = await fetch(`${API_BASE}/recordings/${interview.interview_id}/assembly-status`);
                if (statusResponse.ok) {
                  const status = await statusResponse.json();
                  console.log(`Assembly status (attempt ${attempt + 1}):`, status.status, status.progress ? `${status.progress}%` : '');
                  
                  if (status.status === 'complete') {
                    console.log('Assembly complete!', status.recording_url);
                    setRecordingStatus('complete');
                    trackRecordingUploaded(interview.interview_id, fileSizeMB, 'complete');
                    await triggerTranscription();
                    return status.recording_url;
                  } else if (status.status === 'failed') {
                    console.error('Assembly failed:', status.error);
                    throw new Error(`Assembly failed: ${status.error}`);
                  } else if (status.progress) {
                    setRecordingStatus(`assembling (${status.progress}%)`);
                  }
                }
              } catch (pollError) {
                console.warn('Error polling assembly status:', pollError);
              }
            }
            
            // Timeout - but don't fail, assembly might still complete
            console.warn('Assembly polling timed out, but may still complete in background');
            setRecordingStatus('processing');
            return null;
          }
        }
        
        // If we got here without assembly starting, something went wrong
        throw new Error('Upload completed but assembly did not start');
      }
      
      // For medium files (10-50MB), try direct upload to Supabase Storage
      if (blob.size > 10 * 1024 * 1024) {
        console.log('Large file detected, attempting direct upload...');
        
        try {
          // Step 1: Get signed upload URL
          const urlResponse = await fetch(`${API_BASE}/recordings/get-upload-url`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              interview_id: interview.interview_id,
              file_name: fileName,
              content_type: 'video/webm'
            })
          });
          
          if (urlResponse.ok) {
            const urlResult = await urlResponse.json();
            console.log('Got signed upload URL, uploading directly...');
            
            // Step 2: Upload directly to Supabase Storage
            const uploadResponse = await fetch(urlResult.upload_url, {
              method: 'PUT',
              headers: { 
                'Content-Type': 'video/webm',
                'x-upsert': 'true'
              },
              body: blob
            });
            
            if (uploadResponse.ok) {
              console.log('Direct upload successful, confirming...');
              
              // Step 3: Confirm upload completed
              const confirmResponse = await fetch(`${API_BASE}/recordings/confirm-upload`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                  interview_id: interview.interview_id,
                  storage_path: urlResult.storage_path
                })
              });
              
              if (confirmResponse.ok) {
                const result = await confirmResponse.json();
                console.log('Recording uploaded via direct upload:', result);
                setRecordingStatus('complete');
                
                // Trigger transcription
                await triggerTranscription();
                
                return result.recording_url;
              }
            } else {
              console.log('Direct upload failed with status:', uploadResponse.status);
            }
          }
          
          // If direct upload failed, fall through to chunked upload
          console.log('Direct upload not available, falling back to chunked upload...');
          
          // Use chunked upload as fallback for medium files too
          const CHUNK_SIZE = 10 * 1024 * 1024;
          const totalChunks = Math.ceil(blob.size / CHUNK_SIZE);
          
          for (let i = 0; i < totalChunks; i++) {
            const start = i * CHUNK_SIZE;
            const end = Math.min(start + CHUNK_SIZE, blob.size);
            const chunk = blob.slice(start, end);
            
            const formData = new FormData();
            formData.append('interview_id', interview.interview_id.toString());
            formData.append('token', interview.token);
            formData.append('chunk_index', i.toString());
            formData.append('total_chunks', totalChunks.toString());
            formData.append('file_name', fileName);
            formData.append('file', chunk, `chunk_${i}`);
            
            console.log(`Uploading chunk ${i + 1}/${totalChunks}...`);
            
            const chunkResponse = await fetch(`${API_BASE}/recordings/upload-chunk`, {
              method: 'POST',
              body: formData
            });
            
            if (!chunkResponse.ok) {
              throw new Error(`Chunk upload failed`);
            }
            
            const chunkResult = await chunkResponse.json();
            
            if (chunkResult.complete && chunkResult.recording_url) {
              console.log('Recording uploaded via chunked upload');
              setRecordingStatus('complete');
              await triggerTranscription();
              return chunkResult.recording_url;
            }
          }
        } catch (directErr) {
          console.warn('Direct/chunked upload failed:', directErr);
        }
      }
      
      // Standard base64 upload (for smaller files or as fallback)
      console.log('Using base64 upload method...');
      
      // Convert blob to base64 for upload via API
      const reader = new FileReader();
      const base64Promise = new Promise((resolve) => {
        reader.onloadend = () => {
          const base64 = reader.result.split(',')[1];
          resolve(base64);
        };
        reader.readAsDataURL(blob);
      });
      
      const base64Data = await base64Promise;
      
      // Upload via backend API
      const response = await fetch(`${API_BASE}/recordings/upload`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          interview_id: interview.interview_id,
          file_name: fileName,
          file_data: base64Data,
          content_type: 'video/webm'
        })
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Upload failed: ${response.status} - ${errorText}`);
      }
      
      const result = await response.json();
      console.log('Recording uploaded via base64:', result);
      setRecordingStatus('complete');
      trackRecordingUploaded(interview.interview_id, fileSizeMB, 'complete');
      
      // Trigger transcription
      await triggerTranscription();
      
      return result.url;
      
    } catch (err) {
      console.error('Failed to upload recording:', err);
      setRecordingStatus('error');
      trackRecordingUploaded(interview.interview_id, fileSizeMB, 'error');
      
      // Fallback: offer local download
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `interview_${interview.interview_id}_recording.webm`;
      a.click();
      URL.revokeObjectURL(url);
      
      return null;
    }
  }, [interview, triggerTranscription, sessionNumber]);

  // =============================================================================
  // PAUSE INTERVIEW
  // =============================================================================
  
  const isPausingRef = useRef(false);  // Guard against double pause
  
  const pauseInterview = useCallback(async (reason = 'user_paused') => {
    // Guard against double pause
    if (isPausingRef.current) {
      console.log('Already pausing interview, skipping...');
      return;
    }
    isPausingRef.current = true;
    setPausing(true);
    
    console.log('=== PAUSING INTERVIEW ===');
    console.log('Pause reason:', reason);
    console.log('Recording state:', mediaRecorderRef.current?.state);
    
    // Clear any pending disconnect timeout
    if (disconnectTimeoutRef.current) {
      clearTimeout(disconnectTimeoutRef.current);
      disconnectTimeoutRef.current = null;
    }
    
    try {
      // Stop recording and get blob
      let recordingBlob = null;
      if (isRecording || mediaRecorderRef.current?.state === 'recording') {
        console.log('Stopping recording...');
        recordingBlob = await stopRecording();
        console.log('Recording blob:', recordingBlob ? `${recordingBlob.size} bytes` : 'null');
      }
      
      // Disconnect from LiveKit
      if (roomRef.current) {
        console.log('Disconnecting from LiveKit...');
        roomRef.current.disconnect();
      }
      
      // Clear keep-alive
      if (keepAliveIntervalRef.current) {
        clearInterval(keepAliveIntervalRef.current);
        keepAliveIntervalRef.current = null;
      }
      
      // Update interview status to paused on backend with questions asked
      console.log('Marking interview as paused on backend...');
      console.log('Questions asked:', questionsAskedRef.current.length);
      await fetch(`${API_BASE}/interviews/${interview.interview_id}/status`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          status: 'paused',
          pause_reason: reason,
          questions_asked: questionsAskedRef.current
        })
      }).catch(err => console.warn('Failed to update status:', err));
      
      // Generate reconnect token
      console.log('Generating reconnect token...');
      await fetch(`${API_BASE}/interviews/${interview.interview_id}/generate-reconnect?send_email=true`, {
        method: 'POST'
      }).catch(err => console.warn('Failed to generate reconnect:', err));
      
      // Set pause state
      setPauseReason(reason);
      setIsPaused(true);
      
      // Track interview paused
      const durationSeconds = interviewStartTimeRef.current 
        ? Math.round((Date.now() - interviewStartTimeRef.current) / 1000) 
        : 0;
      trackInterviewPaused(interview.interview_id, reason, durationSeconds);
      
      // Upload recording in background
      if (recordingBlob && recordingBlob.size > 0) {
        console.log('Uploading recording in background...', recordingBlob.size, 'bytes');
        uploadRecording(recordingBlob);
      }
      
    } catch (err) {
      console.error('Error pausing interview:', err);
      // Still show paused state even if error
      setPauseReason(reason);
      setIsPaused(true);
    } finally {
      setPausing(false);
    }
  }, [interview, isRecording, stopRecording, uploadRecording]);

  // Store pauseInterview in ref so it can be called from event handlers
  useEffect(() => {
    pauseInterviewRef.current = pauseInterview;
  }, [pauseInterview]);

  // =============================================================================
  // COMPLETE INTERVIEW
  // =============================================================================
  
  const isCompletingRef = useRef(false);  // Guard against double completion (use ref to avoid stale closure)
  
  const completeInterview = useCallback(async () => {
    // Guard against double completion
    if (isCompletingRef.current) {
      console.log('Already completing interview, skipping...');
      return;
    }
    isCompletingRef.current = true;
    
    console.log('=== COMPLETING INTERVIEW ===');
    console.log('Recording state:', mediaRecorderRef.current?.state);
    console.log('isRecording:', isRecording);
    
    // Clear any pending disconnect timeout
    if (disconnectTimeoutRef.current) {
      clearTimeout(disconnectTimeoutRef.current);
      disconnectTimeoutRef.current = null;
    }
    
    try {
      // Stop recording and get blob
      let recordingBlob = null;
      if (isRecording || mediaRecorderRef.current?.state === 'recording') {
        console.log('Stopping recording...');
        recordingBlob = await stopRecording();
        console.log('Recording blob:', recordingBlob ? `${recordingBlob.size} bytes` : 'null');
      } else {
        console.log('No active recording to stop');
      }
      
      // Disconnect from LiveKit
      if (roomRef.current) {
        console.log('Disconnecting from LiveKit...');
        roomRef.current.disconnect();
      }
      
      // Clear keep-alive
      if (keepAliveIntervalRef.current) {
        clearInterval(keepAliveIntervalRef.current);
        keepAliveIntervalRef.current = null;
      }
      
      // Move to completed stage
      setStage('completed');
      
      // Track interview completed
      const durationSeconds = interviewStartTimeRef.current 
        ? Math.round((Date.now() - interviewStartTimeRef.current) / 1000) 
        : 0;
      trackInterviewCompleted(
        interview?.interview_id, 
        durationSeconds, 
        questionsAskedRef.current.length
      );
      
      // Format real-time transcript for backend
      const formattedTranscript = realTimeTranscriptRef.current.map(entry => 
        `${entry.role === 'interviewer' ? 'Interviewer' : 'Candidate'}: ${entry.text}`
      ).join('\n\n');
      
      // Mark as complete on backend with transcript data
      console.log('Marking interview as complete on backend...');
      console.log('Real-time transcript entries:', realTimeTranscriptRef.current.length);
      console.log('Questions asked:', questionsAskedRef.current.length);
      await fetch(`${API_BASE}/invites/${token}/complete`, { 
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          questions_asked: questionsAskedRef.current,
          real_time_transcript: formattedTranscript,
          transcript_entries: realTimeTranscriptRef.current
        })
      }).catch(err => console.warn('Failed to mark complete:', err));
      
      // Upload recording in background
      if (recordingBlob && recordingBlob.size > 0) {
        console.log('Uploading recording in background...', recordingBlob.size, 'bytes');
        uploadRecording(recordingBlob);
      } else {
        console.warn('No recording blob to upload');
      }
      
    } catch (err) {
      console.error('Error completing interview:', err);
      setStage('completed'); // Still complete even if error
    }
  }, [token, isRecording, stopRecording, uploadRecording, interview]);

  // Store completeInterview in ref so it can be called from event handlers
  useEffect(() => {
    completeInterviewRef.current = completeInterview;
  }, [completeInterview]);

  // =============================================================================
  // BRANDING / STYLES
  // =============================================================================
  
  // Helper to detect if a color is light (needs dark text) or dark (needs light text)
  const isLightColor = (color) => {
    if (!color) return false;
    
    // Handle hex colors
    let hex = color.replace('#', '');
    if (hex.length === 3) {
      hex = hex.split('').map(c => c + c).join('');
    }
    
    if (!/^[0-9A-Fa-f]{6}$/.test(hex)) {
      return false; // Default to dark background if can't parse
    }
    
    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);
    
    // Calculate relative luminance (perceived brightness)
    const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
    
    return luminance > 0.5; // Light if luminance > 50%
  };
  
  const branding = interview?.branding || {};
  const backgroundColor = branding.backgroundColor || branding.headerGradient || '#0a0f14';
  
  // Auto-detect text color based on background brightness
  const isLight = isLightColor(backgroundColor);
  const textColor = branding.textColor || (isLight ? '#1f2937' : '#ffffff');
  const mutedTextColor = isLight ? 'rgba(31, 41, 55, 0.7)' : 'rgba(255, 255, 255, 0.7)';
  const cardBg = isLight ? 'rgba(0, 0, 0, 0.05)' : 'rgba(255, 255, 255, 0.05)';
  const cardBorder = isLight ? 'rgba(0, 0, 0, 0.1)' : 'rgba(255, 255, 255, 0.1)';
  const spinnerTrack = isLight ? 'rgba(0, 0, 0, 0.1)' : 'rgba(255, 255, 255, 0.1)';
  
  // Container style with CSS custom properties for cascading colors
  const containerStyle = {
    background: backgroundColor,
    minHeight: '100vh',
    color: textColor,
    // CSS custom properties that cascade to all children via the CSS file
    '--text-color': textColor,
    '--muted-text-color': mutedTextColor,
    '--card-bg': cardBg,
    '--card-border': cardBorder,
    '--spinner-track': spinnerTrack
  };
  const primaryColor = branding.primaryColor || '#3ECF8E';
  const buttonBg = branding.buttonPrimaryBg || primaryColor;
  const buttonText = branding.buttonTextColor || '#000000';


  // Use first name only
  const candidateFirstName = (interview?.candidate_name || '').split(' ')[0] || 'there';

  // =============================================================================
  // RENDER: LOADING
  // =============================================================================
  
  if (loading) {
    return (
      <div className="interview-page" style={containerStyle}>
        <div className="loading-container">
          <div className="spinner"></div>
          <p>Loading interview...</p>
        </div>
      </div>
    );
  }

  // =============================================================================
  // RENDER: ERROR
  // =============================================================================
  
  if (error && !interview) {
    return (
      <div className="interview-page" style={containerStyle}>
        <div className="error-container">
          <div className="error-icon">⚠️</div>
          <h2>Unable to Load Interview</h2>
          <p>{error}</p>
          <p className="error-help">
            If you believe this is an error, please contact the recruiter who sent you this link.
          </p>
        </div>
      </div>
    );
  }
  
  // =============================================================================
  // RENDER: CAPACITY WAIT
  // =============================================================================
  
  if (capacityWait) {
    return (
      <CapacityWait
        jobTitle={capacityWait.job_title}
        companyName={capacityWait.company_name}
        candidateEmail={capacityWait.candidate_email}
        message={capacityWait.message}
      />
    );
  }

  // =============================================================================
  // RENDER: PAUSED STATE
  // =============================================================================
  
  if (isPaused) {
    return (
      <div className="interview-page" style={containerStyle}>
        <div className="paused-container">
          <div className="pause-icon">⏸️</div>
          <h1>Interview Paused</h1>
          {pauseReason === 'disconnected' ? (
            <p>
              It looks like we lost the connection. Don't worry — your responses 
              up to this point have been saved. A link to resume your interview 
              will be sent to your email shortly.
            </p>
          ) : (
            <p>
              Your interview has been paused and your progress has been saved. 
              A link to resume your interview will be sent to your email.
            </p>
          )}
          
          {/* Recording upload status */}
          {recordingStatus === 'uploading' && (
            <div className="upload-status">
              <div className="spinner small"></div>
              <p>Saving your responses...</p>
            </div>
          )}
          {recordingStatus === 'complete' && (
            <p className="upload-success">✓ Responses saved successfully</p>
          )}
          {recordingStatus === 'error' && (
            <p className="upload-error">
              ⚠ We had trouble saving. A backup copy has been downloaded to your device.
            </p>
          )}
          
          <p className="close-message">
            Questions? Contact the hiring team at {interview?.company_name || 'the company'}.
          </p>
        </div>
      </div>
    );
  }

  // =============================================================================
  // RENDER: SETUP STAGE (combined welcome + camera/mic check)
  // =============================================================================
  
  if (stage === 'setup') {
    return (
      <div className="interview-page" style={containerStyle}>
        <div className="setup-container">
          {/* Header with logo and welcome */}
          <div className="setup-header">
            {branding.logoUrl && (
              <img src={branding.logoUrl} alt={interview.company_name} className="company-logo" />
            )}
            <h1>Welcome, {candidateFirstName}!</h1>
            <div className="interview-info">
              <p className="job-title">{interview.job_title}</p>
              <p className="company-name" style={{ color: mutedTextColor }}>at {interview.company_name}</p>
            </div>
          </div>
          
          {/* Camera preview */}
          <div className="setup-preview">
            {mediaStream ? (
              <video 
                ref={videoPreviewRef} 
                autoPlay 
                playsInline 
                muted 
                className="preview-video"
              />
            ) : (
              <div className="preview-placeholder">
                <span>📷</span>
                <p>{mediaError ? 'Camera access needed' : 'Starting camera...'}</p>
              </div>
            )}
          </div>
          
          {mediaError && (
            <div className="media-error">
              <p>{mediaError}</p>
              <button onClick={startMedia} className="retry-button">
                Try Again
              </button>
            </div>
          )}
          
          {/* Media controls */}
          <div className="media-controls">
            <button 
              onClick={toggleCamera} 
              className={`media-button ${cameraEnabled ? 'active' : 'inactive'}`}
              style={cameraEnabled ? { backgroundColor: `${buttonBg}33`, borderColor: buttonBg, color: buttonBg } : {}}
            >
              {cameraEnabled ? '📷 Camera On' : '📷 Camera Off'}
            </button>
            <button 
              onClick={toggleMic} 
              className={`media-button ${micEnabled ? 'active' : 'inactive'}`}
              style={micEnabled ? { backgroundColor: `${buttonBg}33`, borderColor: buttonBg, color: buttonBg } : {}}
            >
              {micEnabled ? '🎤 Mic On' : '🔇 Mic Off'}
            </button>
            <button 
              onClick={() => setShowAccommodationsModal(true)}
              className="media-button"
              style={{ marginLeft: '12px' }}
            >
              ♿ Accommodations
            </button>
          </div>
          
          {/* Tips */}
          <p className="setup-tip">
            Find a quiet, well-lit space • This takes no more than 20 minutes
          </p>
          
          {/* Join button */}
          <button 
            onClick={proceedToConsent}
            disabled={!micEnabled || !mediaStream}
            className="primary-button join-button"
            style={{ 
              backgroundColor: (micEnabled && mediaStream) ? buttonBg : '#444',
              color: (micEnabled && mediaStream) ? buttonText : '#888'
            }}
          >
            Join Interview
          </button>
          
          {!micEnabled && mediaStream && (
            <p className="mic-warning">⚠️ Please enable your microphone to continue</p>
          )}
        </div>
        {/* Accommodations Modal */}
{showAccommodationsModal && (
  <div 
    className="modal-overlay"
    style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.85)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000
    }}
    onClick={() => setShowAccommodationsModal(false)}
  >
    <div 
      className="modal-content"
      style={{
        backgroundColor: '#1a1a1a',
        borderRadius: '12px',
        padding: '32px',
        maxWidth: '500px',
        width: '90%',
        maxHeight: '80vh',
        overflow: 'auto'
      }}
      onClick={(e) => e.stopPropagation()}
    >
      <h2 style={{ margin: '0 0 8px 0', color: '#fff', fontSize: '24px' }}>
        Accessibility Accommodations
      </h2>
      <p style={{ color: '#888', marginBottom: '24px', fontSize: '14px' }}>
        Select any accommodations you need. This is confidential and won't affect your evaluation.
      </p>
      
      {/* Extended Time */}
      <label style={{ display: 'flex', alignItems: 'flex-start', gap: '12px', marginBottom: '16px', cursor: 'pointer' }}>
        <input 
          type="checkbox"
          checked={accommodations.extendedTime}
          onChange={(e) => setAccommodations(prev => ({ ...prev, extendedTime: e.target.checked }))}
          style={{ marginTop: '4px' }}
        />
        <div>
          <strong style={{ color: '#e0e0e0' }}>Extended Time</strong>
          <p style={{ color: '#888', fontSize: '13px', margin: '2px 0 0 0' }}>Additional time to complete</p>
        </div>
      </label>
      {accommodations.extendedTime && (
        <select 
          value={accommodations.timeMultiplier}
          onChange={(e) => setAccommodations(prev => ({ ...prev, timeMultiplier: parseFloat(e.target.value) }))}
          style={{ 
            marginLeft: '28px', marginBottom: '16px', padding: '8px', 
            backgroundColor: '#0a0a0a', border: '1px solid #333', borderRadius: '4px', color: '#e0e0e0'
          }}
        >
          <option value={1.5}>1.5x (30 min)</option>
          <option value={2}>2x (40 min)</option>
          <option value={0}>Unlimited</option>
        </select>
      )}
      
      {/* Text Questions */}
      <label style={{ display: 'flex', alignItems: 'flex-start', gap: '12px', marginBottom: '16px', cursor: 'pointer' }}>
        <input 
          type="checkbox"
          checked={accommodations.textQuestions}
          onChange={(e) => setAccommodations(prev => ({ ...prev, textQuestions: e.target.checked }))}
          style={{ marginTop: '4px' }}
        />
        <div>
          <strong style={{ color: '#e0e0e0' }}>Display Questions as Text</strong>
          <p style={{ color: '#888', fontSize: '13px', margin: '2px 0 0 0' }}>For deaf/hard of hearing</p>
        </div>
      </label>
      
      {/* Text Responses */}
      <label style={{ display: 'flex', alignItems: 'flex-start', gap: '12px', marginBottom: '16px', cursor: 'pointer' }}>
        <input 
          type="checkbox"
          checked={accommodations.textResponses}
          onChange={(e) => setAccommodations(prev => ({ ...prev, textResponses: e.target.checked }))}
          style={{ marginTop: '4px' }}
        />
        <div>
          <strong style={{ color: '#e0e0e0' }}>Type Responses</strong>
          <p style={{ color: '#888', fontSize: '13px', margin: '2px 0 0 0' }}>Answer by typing instead of speaking</p>
        </div>
      </label>
      
      {/* Large Text */}
      <label style={{ display: 'flex', alignItems: 'flex-start', gap: '12px', marginBottom: '16px', cursor: 'pointer' }}>
        <input 
          type="checkbox"
          checked={accommodations.largeText}
          onChange={(e) => setAccommodations(prev => ({ ...prev, largeText: e.target.checked }))}
          style={{ marginTop: '4px' }}
        />
        <div>
          <strong style={{ color: '#e0e0e0' }}>Larger Text</strong>
        </div>
      </label>
      
      {/* High Contrast */}
      <label style={{ display: 'flex', alignItems: 'flex-start', gap: '12px', marginBottom: '16px', cursor: 'pointer' }}>
        <input 
          type="checkbox"
          checked={accommodations.highContrast}
          onChange={(e) => setAccommodations(prev => ({ ...prev, highContrast: e.target.checked }))}
          style={{ marginTop: '4px' }}
        />
        <div>
          <strong style={{ color: '#e0e0e0' }}>High Contrast</strong>
        </div>
      </label>
      
      {/* Reduced Motion */}
      <label style={{ display: 'flex', alignItems: 'flex-start', gap: '12px', marginBottom: '24px', cursor: 'pointer' }}>
        <input 
          type="checkbox"
          checked={accommodations.reducedMotion}
          onChange={(e) => setAccommodations(prev => ({ ...prev, reducedMotion: e.target.checked }))}
          style={{ marginTop: '4px' }}
        />
        <div>
          <strong style={{ color: '#e0e0e0' }}>Reduced Motion</strong>
        </div>
      </label>
      
      {/* Close button */}
      <button 
        onClick={() => setShowAccommodationsModal(false)}
        className="primary-button"
        style={{ width: '100%', backgroundColor: primaryColor }}
      >
        Done
      </button>
    </div>
  </div>
)}
      </div>
    );
  }

  // =============================================================================
  // RENDER: CONSENT STAGE
  // =============================================================================
  
  if (stage === 'consent') {
    return (
      <div className="interview-page" style={containerStyle}>
        <div className="consent-container">
          {/* Header */}
          <div className="consent-header">
            {branding.logoUrl && (
              <img src={branding.logoUrl} alt={interview.company_name} className="company-logo small" />
            )}
            <h2>Recording Consent</h2>
          </div>
          
          {/* Consent notice */}
          <div className="consent-notice">
            <div className="consent-icon">🎥</div>
            <p className="consent-main">
              This interview will be <strong>recorded</strong> (video and audio) to help {interview.company_name} evaluate your candidacy.
            </p>
            <p className="consent-details">
              The recording will be shared with the hiring team at <strong>{interview.company_name}</strong> and will be stored securely. 
              It will only be used for hiring evaluation purposes.
            </p>
          </div>
          
          {/* Consent checkbox */}
          <label className="consent-checkbox-label">
            <input 
              type="checkbox"
              checked={recordingConsent}
              onChange={(e) => setRecordingConsent(e.target.checked)}
              className="consent-checkbox"
            />
            <span className="consent-checkbox-text">
              I understand and consent to being recorded during this interview. I agree that the recording 
              may be reviewed by {interview.company_name}'s hiring team.
            </span>
          </label>
          
          {/* Buttons */}
          <div className="consent-buttons">
            <button 
              onClick={() => setStage('setup')}
              className="secondary-button"
            >
              ← Go Back
            </button>
            <button 
              onClick={startInterview}
              disabled={!recordingConsent}
              className="primary-button"
              style={{ 
                backgroundColor: recordingConsent ? buttonBg : '#444',
                color: recordingConsent ? buttonText : '#888'
              }}
            >
              Continue with Recording
            </button>
          </div>
          
          {!recordingConsent && (
            <p className="consent-required">
              Please check the box above to continue
            </p>
          )}
        </div>
      </div>
    );
  }

  // =============================================================================
  // RENDER: CONNECTING STAGE
  // =============================================================================
  
  if (stage === 'connecting') {
    return (
      <div className="interview-page" style={containerStyle}>
        <div className="connecting-container">
          <div className="spinner large"></div>
          <h2>Connecting to your interview...</h2>
          <p className="connecting-status">
            {connectionStatus === 'starting' && 'Preparing your session...'}
            {connectionStatus === 'connecting' && 'Connecting to video...'}
          </p>
        </div>
      </div>
    );
  }

  // =============================================================================
  // RENDER: INTERVIEW STAGE
  // =============================================================================
  
  if (stage === 'interview') {
    // Build accommodation CSS classes
    const accommodationClasses = [
      'interview-page',
      'interview-active',
      accommodations.largeText && 'large-print',
      accommodations.highContrast && 'high-contrast',
      accommodations.reducedMotion && 'reduced-motion'
    ].filter(Boolean).join(' ');
    
    return (
      <div className={accommodationClasses} style={containerStyle}>
        <div className="interview-container">
          {/* Recording indicator */}
          {isRecording && (
            <div className="recording-indicator">
              <span className="recording-dot"></span>
              <span>Recording</span>
            </div>
          )}
          
          {/* Avatar video */}
          <div className="avatar-container">
            <video 
              ref={avatarVideoRef}
              autoPlay 
              playsInline
              className="avatar-video"
            />
            
            {/* Text display for questions (accessibility) */}
            {accommodations.textQuestions && currentQuestionText && (
              <div 
                className="question-text-display"
                role="region"
                aria-live="polite"
                aria-label="Current interview question"
                style={{
                  position: 'absolute',
                  bottom: '20px',
                  left: '20px',
                  right: '20px',
                  backgroundColor: 'rgba(0, 0, 0, 0.85)',
                  padding: '16px 20px',
                  borderRadius: '8px',
                  color: '#fff',
                  fontSize: accommodations.largeText ? '20px' : '16px',
                  lineHeight: '1.5',
                  maxHeight: '30%',
                  overflow: 'auto'
                }}
              >
                <strong>Question:</strong> {currentQuestionText}
              </div>
            )}

            {connectionStatus !== 'connected' && (
              <div className="avatar-overlay">
                <div className="spinner"></div>
                <p>
                  {connectionStatus === 'reconnecting' && 'Reconnecting...'}
                  {connectionStatus === 'connecting' && 'Connecting...'}
                  {connectionStatus === 'disconnected' && 'Connection lost. Attempting to reconnect...'}
                </p>
                {connectionStatus === 'disconnected' && wasConnected && (
                  <button 
                    onClick={() => pauseInterview('disconnected')}
                    className="secondary-button"
                    style={{ marginTop: '16px' }}
                  >
                    Save & Exit
                  </button>
                )}
              </div>
            )}
          </div>
          
          {/* Self view (small) */}
          <div className="self-view">
            <video 
              ref={(el) => {
                videoPreviewRef.current = el;
                if (el && mediaStreamRef.current) {
                  el.srcObject = mediaStreamRef.current;
                }
              }}
              autoPlay 
              playsInline 
              muted
              className="self-video"
            />
            <div className="self-audio-level">
              <div 
                className="self-audio-fill"
                style={{ height: `${audioLevel}%`, backgroundColor: primaryColor }}
              />
            </div>
          </div>
          
          {/* Intro notice / Mic status */}
          {!introComplete ? (
            <div className="intro-notice">
              <p>🎧 Listening to introduction... Your mic will be enabled shortly.</p>
            </div>
          ) : (
            <div className="intro-notice ready">
              <p>{micEnabled ? '🎤 Your turn to speak' : '🔇 Click mic to unmute'}</p>
            </div>
          )}
          
          {/* Controls */}
          <div className="interview-controls">
            <button 
              onClick={toggleMic}
              className={`control-button ${micEnabled ? 'active' : 'muted'}`}
              disabled={!introComplete}
              title={!introComplete ? 'Mic will be enabled after introduction' : ''}
            >
              {micEnabled ? '🎤' : '🔇'}
            </button>
            <button 
              onClick={toggleCamera}
              className={`control-button ${cameraEnabled ? 'active' : 'muted'}`}
            >
              {cameraEnabled ? '📷' : '📷'}
            </button>
            <button 
              onClick={() => pauseInterview('user_paused')}
              className="control-button pause-call"
              disabled={pausing}
              title="Pause and save progress"
            >
              {pausing ? '...' : '⏸️'}
            </button>
            <button 
              onClick={completeInterview}
              className="control-button end-call"
            >
              End Interview
            </button>
          </div>
        </div>
      </div>
    );
  }

  // =============================================================================
  // RENDER: COMPLETED STAGE
  // =============================================================================
  
  if (stage === 'completed') {
    const config = interview?.interview_config || {};
    const thankYouHeading = config.thank_you_heading?.replace('{company_name}', interview.company_name) 
      || `Thank you for interviewing with ${interview.company_name}`;
    const thankYouBody = config.thank_you_body?.replace('{company_name}', interview.company_name)
      || 'Your responses have been recorded and will be reviewed by our team.';
    
    return (
      <div className="interview-page" style={containerStyle}>
        <div className="completed-container">
          <div className="success-icon">✓</div>
          <h1>{thankYouHeading}</h1>
          <p>{thankYouBody}</p>
          
          {/* Recording upload status */}
          {recordingStatus === 'uploading' && (
            <div className="upload-status">
              <div className="spinner small"></div>
              <p>Saving your interview recording...</p>
            </div>
          )}
          {recordingStatus === 'complete' && (
            <p className="upload-success">✓ Recording saved successfully</p>
          )}
          {recordingStatus === 'error' && (
            <p className="upload-error">
              Recording could not be uploaded automatically. A backup copy has been downloaded to your device.
            </p>
          )}
          
          <p className="close-message">You may now close this window.</p>
        </div>
      </div>
    );
  }

  return null;
}
